import {
  Box,
  Divider,
  Grid,
  InputAdornment,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { Button } from "@mui/material";
import { Field } from "formik";
import React, { useEffect, useState } from "react";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  Save as SaveIcon,
  KeyboardArrowDown,
  KeyboardArrowUp,
} from "@mui/icons-material";
import PaymentDetails from "./PaymentDetails";
import DeletedIncomeDetails from "./DeletedIncomeDetails";
import { bookingApi } from "../apiService";
import DeletedPayments from "./DeletedPayments";
import { calculateAmount } from "../utils/calculateAmounts";
import { commonTextFieldProps, smallButtonProp } from "../../../utils/styles";
import GSTView from "../../../components/GSTView";

const FixedIncome = ({
  income,
  index,
  values,
  setFieldValue,
  setIncomeSummary,
  additionalFields,
  setAdditionalFields,
  formatCurrency,
  calculateIncomeSummary,
  errors,
  gstPercentage,
}: any) => {
  const [isEditMode, setIsEditMode] = useState(false);
  useEffect(() => {
    setFieldValue(`incomes.${index}.gstPercentage`, gstPercentage);
  }, [gstPercentage]);

  const checkDisabled = () => {
    if (!income?.id) return false;
    return !isEditMode;
  };

  return (
    <>
      {["perkm", "fixed"].includes(income?.incomeType) && (
        <>
          <Grid container spacing={1} alignItems="center">
            <Grid item md={10}>
              <Typography variant="h6" sx={{ mb: 1, fontSize: "0.9rem" }}>
                Income Details
              </Typography>
            </Grid>
            <Grid
              item
              md={2}
              sx={{ display: "flex", justifyContent: "flex-end" }}
            >
              {!isEditMode && income?.id && (
                <Button
                  size="small"
                  sx={{
                    height: "20px",
                    width: "50px",
                    fontSize: "0.6rem",
                  }}
                  variant="contained"
                  color="primary"
                  onClick={() => {
                    setIsEditMode(true);
                  }}
                >
                  Edit
                </Button>
              )}
              {isEditMode && income?.id && (
                <Button
                  size="small"
                  sx={{
                    height: "20px",
                    width: "50px",
                    fontSize: "0.6rem",
                  }}
                  variant="contained"
                  color="error"
                  onClick={() => {
                    setIsEditMode(false);
                  }}
                >
                  Cancel
                </Button>
              )}
            </Grid>
          </Grid>
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <Box>
                <Typography
                  variant="subtitle1"
                  sx={{
                    mb: 1,
                    fontWeight: 500,
                    fontSize: "0.75rem",
                  }}
                >
                  Income Type
                </Typography>
                <ToggleButtonGroup
                  value={income?.incomeType}
                  exclusive
                  onChange={(_, newValue) => {
                    if (newValue) {
                      setFieldValue(`incomes.${index}.incomeType`, newValue);
                      setFieldValue(`incomes.${index}.amount`, 0);
                      setFieldValue(`incomes.${index}.limit`, 0);
                      setFieldValue(`incomes.${index}.overLimitRate`, 0);
                      setAdditionalFields({
                        sgstAmount: 0,
                        cgstAmount: 0,
                        totalAmount: 0,
                        totalAmountExclGst: 0,
                        balanceAmount: 0,
                      });
                      values?.incomes?.length > 0 &&
                        calculateIncomeSummary(
                          values?.incomes,
                          setIncomeSummary
                        );
                    }
                  }}
                  disabled={Boolean(income?.id)}
                  aria-label="income type"
                  sx={{
                    "& .MuiToggleButton-root": {
                      px: 2,
                      py: 0.5,
                      fontSize: "0.6rem",
                      textTransform: "none",
                    },
                  }}
                >
                  <ToggleButton
                    value="perkm"
                    aria-label="per km"
                    sx={{ textTransform: "none" }}
                  >
                    Per km
                  </ToggleButton>
                  <ToggleButton
                    value="fixed"
                    aria-label="fixed"
                    sx={{ textTransform: "none" }}
                  >
                    Fixed
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>
            </Grid>

            <Grid item container spacing={1}>
              <Grid container item xs={12} md={3}>
                <Grid item xs={12} md={12}>
                  <Typography variant="h6" sx={{ fontSize: "0.75rem", mb: 1 }}>
                    Amount
                  </Typography>
                </Grid>
                <Grid item xs={12} md={11}>
                  <Box
                    sx={{
                      p: 1,
                      pt: 2,
                      borderRadius: 1,
                      border: "1px solid",
                      borderColor: "grey.300",
                      bgcolor: "#fdfdfd",
                    }}
                  >
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={12}>
                        <Field
                          as={TextField}
                          fullWidth
                          label={
                            income?.incomeType === "perkm"
                              ? "Amount (₹)"
                              : "Fixed Amount (₹)"
                          }
                          name={
                            income?.incomeType === "perkm"
                              ? additionalFields?.fixedOrPerKmIncomeExclGst
                              : `incomes.${index}.amount`
                          }
                          value={
                            income?.incomeType === "perkm"
                              ? additionalFields?.fixedOrPerKmIncomeExclGst &&
                                additionalFields?.fixedOrPerKmIncomeExclGst >= 0
                                ? Number(
                                    additionalFields?.fixedOrPerKmIncomeExclGst
                                  ).toLocaleString("en-IN")
                                : ""
                              : income?.amount && income.amount >= 0
                              ? Number(income.amount).toLocaleString("en-IN")
                              : ""
                          }
                          onChange={(
                            e: React.ChangeEvent<HTMLInputElement>
                          ) => {
                            const cleanValue = e.target.value.replace(
                              /[^0-9]/g,
                              ""
                            );
                            setFieldValue(
                              `incomes.${index}.amount`,
                              cleanValue
                            );
                            setFieldValue(`incomes.${index}.isEdited`, true);
                          }}
                          onBlur={() => {
                            calculateAmount(
                              values?.incomes,
                              setAdditionalFields
                            );
                            values?.incomes?.length > 0 &&
                              calculateIncomeSummary(
                                values?.incomes,
                                setIncomeSummary
                              );
                          }}
                          InputProps={{
                            inputProps: { min: 0 },
                            startAdornment: (
                              <InputAdornment position="start">
                                ₹
                              </InputAdornment>
                            ),
                          }}
                          required
                          {...commonTextFieldProps}
                          disabled={
                            checkDisabled() || income?.incomeType === "perkm"
                          }
                          error={Boolean(
                            errors?.incomes?.[index]?.amount &&
                              income?.incomeType === "fixed"
                          )}
                          helperText={errors?.incomes?.[index]?.amount}
                        />
                      </Grid>
                    </Grid>
                  </Box>
                </Grid>
              </Grid>
              {income?.incomeType !== "fixed" && (
                <Grid container item md={9} spacing={2} sx={{ p: 0 }}>
                  <Grid item xs={12} md={12}>
                    <Typography variant="h6" sx={{ fontSize: "0.75rem" }}>
                      Booking Conditions
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Field
                      as={TextField}
                      fullWidth
                      label="Limit (km)"
                      name={`incomes.${index}.limit`}
                      value={
                        income?.limit && income.limit >= 0
                          ? Number(income.limit).toLocaleString("en-IN")
                          : ""
                      }
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        const cleanValue = e.target.value.replace(
                          /[^0-9]/g,
                          ""
                        );
                        setFieldValue(`incomes.${index}.limit`, cleanValue);
                        setFieldValue(`incomes.${index}.isEdited`, true);
                      }}
                      onBlur={() => {
                        calculateAmount(values?.incomes, setAdditionalFields);
                        values?.incomes?.length > 0 &&
                          calculateIncomeSummary(
                            values?.incomes,
                            setIncomeSummary
                          );
                      }}
                      InputProps={{
                        inputProps: { min: 0 },
                      }}
                      required={income?.incomeType !== "fixed"}
                      {...commonTextFieldProps}
                      disabled={checkDisabled()}
                      error={Boolean(errors?.incomes?.[index]?.limit)}
                      helperText={errors?.incomes?.[index]?.limit}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Field
                      as={TextField}
                      fullWidth
                      label={
                        income?.incomeType === "perton"
                          ? "Rate (₹/ton)"
                          : "Rate (₹/km)"
                      }
                      name={`incomes.${index}.amount`}
                      value={
                        income?.amount && income.amount >= 0
                          ? Number(income.amount).toLocaleString("en-IN")
                          : ""
                      }
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        const cleanValue = e.target.value.replace(
                          /[^0-9]/g,
                          ""
                        );
                        setFieldValue(`incomes.${index}.amount`, cleanValue);
                        setFieldValue(`incomes.${index}.isEdited`, true);
                      }}
                      onBlur={() => {
                        calculateAmount(values?.incomes, setAdditionalFields);
                        values?.incomes?.length > 0 &&
                          calculateIncomeSummary(
                            values?.incomes,
                            setIncomeSummary
                          );
                      }}
                      InputProps={{
                        inputProps: { min: 0 },
                        startAdornment: (
                          <InputAdornment position="start">₹</InputAdornment>
                        ),
                      }}
                      required={income?.incomeType !== "fixed"}
                      {...commonTextFieldProps}
                      disabled={checkDisabled()}
                      error={Boolean(errors?.incomes?.[index]?.amount)}
                      helperText={errors?.incomes?.[index]?.amount}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Field
                      as={TextField}
                      fullWidth
                      label={
                        income?.incomeType === "perton"
                          ? "Over Limit Rate (₹/ton)"
                          : "Over Limit Rate (₹/km)"
                      }
                      name={`incomes.${index}.overLimitRate`}
                      value={
                        income?.overLimitRate && income.overLimitRate >= 0
                          ? Number(income.overLimitRate).toLocaleString("en-IN")
                          : ""
                      }
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        const cleanValue = e.target.value.replace(
                          /[^0-9]/g,
                          ""
                        );
                        setFieldValue(
                          `incomes.${index}.overLimitRate`,
                          cleanValue
                        );
                        setFieldValue(`incomes.${index}.isEdited`, true);
                      }}
                      InputProps={{
                        inputProps: { min: 0 },
                        startAdornment: (
                          <InputAdornment position="start">₹</InputAdornment>
                        ),
                      }}
                      {...commonTextFieldProps}
                      disabled={checkDisabled()}
                      error={Boolean(errors?.incomes?.[index]?.overLimitRate)}
                      helperText={errors?.incomes?.[index]?.overLimitRate}
                    />
                  </Grid>
                  {/* <>
                    <Grid item xs={0} sm={0} md={12} />
                    <Grid item xs={0} sm={0} md={12} />
                    {income?.gstPercentage > 0 &&
                      (income?.amount > 0 ||
                        additionalFields?.fixedOrPerKmIncomeExclGst > 0) && (
                        <>
                          <Grid item xs={0} sm={0} md={12} />
                          <Grid item xs={0} sm={0} md={12} />
                          <Grid item xs={0} sm={0} md={12} />
                          <Grid item xs={0} sm={0} md={12} />
                          <Grid item xs={0} sm={0} md={12} />
                        </>
                      )}
                  </> */}
                </Grid>
              )}
            </Grid>
          </Grid>
        </>
      )}
    </>
  );
};

export default FixedIncome;
